import { Component, ElementRef, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { timeout } from 'rxjs/operators';
import { Member } from 'src/app/core/_models/member';
import { RegisterRequest } from 'src/app/core/_models/register-request';
import { AccountService } from 'src/app/core/_services/account.service';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';
import { MemberService } from 'src/app/core/_services/member.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { UserService } from 'src/app/core/_services/user.service';
import { FRANCE_CODE } from 'src/app/core/_utils/countries';

type ProfileType = 'entreprise_tp' | 'moe' | 'moa' | 'etudiant' | 'fabricant';
type Adhesion = 'adherente' | 'non_adherente';

// Le 9e chiffre du SIREN est une clé de Luhn.
const sirenKeyValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value: string = control.value ?? '';
  if (!/^\d{9}$/.test(value)) {
    return null;
  }
  const sum = [...value].reverse().reduce((acc, char, i) => {
    const digit = i % 2 === 1 ? Number(char) * 2 : Number(char);
    return acc + (digit > 9 ? digit - 9 : digit);
  }, 0);
  return sum % 10 === 0 ? null : { sirenKey: true };
};

@Component({
  selector: 'app-inscription',
  templateUrl: './inscription.component.html',
  styleUrls: ['./inscription.component.scss']
})
export class InscriptionComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  searching = false;
  searched = false;
  isOther = false;
  showConfirm = false;
  orgWasFound = false;
  emailQuestion = false;
  showMissingFields = false;
  existingAccountsPrompt = false;
  sendingExistingAccounts = false;
  existingAccountsSent = false;
  private checkedEmail = '';
  private blockedEmailDomains = new Set<string>();

  foundOrganisations: Member[] = [];
  referents: string[] = [];
  featuredCountries: { code: string; name: string }[] = [];
  countries: { code: string; name: string }[] = [];

  profileTypes: { value: ProfileType; label: string }[] = [
    { value: 'entreprise_tp', label: 'inscription_profile_entreprise_tp' },
    { value: 'moa', label: 'inscription_profile_moa' },
    { value: 'moe', label: 'inscription_profile_moe' },
    { value: 'fabricant', label: 'inscription_profile_manufacturer' },
    { value: 'etudiant', label: 'inscription_profile_student' }
  ];

  private readonly step1Fields = ['profileType', 'nom', 'prenom', 'email', 'fonction'];

  adhesions: { value: Adhesion; label: string }[] = [
    { value: 'adherente', label: 'inscription_fntp_member' },
    { value: 'non_adherente', label: 'inscription_fntp_non_member' }
  ];

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly memberService: MemberService,
    private readonly accountService: AccountService,
    private readonly userService: UserService,
    private readonly snackbar: SnackbarService,
    private readonly labelI18n: LabelI18nService
  ) {}

  ngOnInit(): void {
    this.featuredCountries = this.labelI18n.featuredCountries;
    this.countries = this.labelI18n.countries;
    this.form = this.formBuilder.group({
      profileType: [null, Validators.required],
      nom: ['', Validators.required],
      prenom: ['', Validators.required],
      email: ['', [Validators.required, Validators.email, this.allowedEmailDomainValidator]],
      fonction: ['', Validators.required],
      pays: [FRANCE_CODE],
      organisation: [''],
      siren: [''],
      adhesion: [null],
      numeroTp: [''],
      adresse: [''],
      codePostal: [''],
      ville: [''],
      organisationTrouvee: [''],
      cgu: [false]
    });
    this.accountService.getBlockedEmailDomains().subscribe({
      next: (domains) => {
        this.blockedEmailDomains = new Set(domains.map((d) => d.toLowerCase()));
        this.f.email.updateValueAndValidity();
      },
      error: () => this.snackbar.open('error')
    });
    this.f.email.valueChanges.subscribe(() => {
      if (this.searched) {
        this.resetSearch();
        this.applyConditionalValidators();
      }
    });
  }

  private readonly allowedEmailDomainValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const domain = (control.value ?? '').split('@').pop().toLowerCase();
    return this.blockedEmailDomains.has(domain) ? { blockedDomain: true } : null;
  };

  get f() {
    return this.form.controls;
  }

  get isEntrepriseTp(): boolean {
    return this.f.profileType.value === 'entreprise_tp';
  }

  get isStudent(): boolean {
    return this.f.profileType.value === 'etudiant';
  }

  get isFrance(): boolean {
    return this.f.pays.value === FRANCE_CODE;
  }

  get isAdherente(): boolean {
    return this.f.adhesion.value === 'adherente';
  }

  get isNonAdherente(): boolean {
    return this.f.adhesion.value === 'non_adherente';
  }

  get orgFound(): boolean {
    return this.searched && this.orgWasFound;
  }

  get orgNotFound(): boolean {
    return this.searched && (!this.orgWasFound || this.isOther);
  }

  showCheck(name: string): boolean {
    return this.f[name].valid && !!this.f[name].value;
  }

  selectProfile(value: ProfileType): void {
    this.f.profileType.setValue(value);
    if (this.isStudent) {
      this.f.fonction.setValue('');
    }
    this.applyConditionalValidators();
  }

  selectAdhesion(value: Adhesion): void {
    this.f.adhesion.setValue(value);
    this.applyConditionalValidators();
  }

  onSelectPays(): void {
    this.applyConditionalValidators();
  }

  onEmailBlur(): void {
    const email = this.f.email.value;
    if (this.f.email.invalid || email === this.checkedEmail) {
      return;
    }
    this.userService.emailHasAccount(email).subscribe({
      next: (hasAccount) => {
        this.checkedEmail = email;
        this.emailQuestion = hasAccount;
      },
      error: () => this.snackbar.open('error')
    });
  }

  keepRegistering(): void {
    this.emailQuestion = false;
  }

  showExistingAccounts(): void {
    this.existingAccountsPrompt = true;
  }

  cancelExistingAccounts(): void {
    this.existingAccountsPrompt = false;
  }

  sendExistingAccounts(): void {
    this.sendingExistingAccounts = true;
    this.userService.sendExistingAccounts(this.f.email.value).subscribe({
      next: () => {
        this.sendingExistingAccounts = false;
        this.emailQuestion = false;
        this.existingAccountsPrompt = false;
        this.existingAccountsSent = true;
      },
      error: () => {
        this.sendingExistingAccounts = false;
        this.snackbar.open('error');
      }
    });
  }

  closeExistingAccounts(): void {
    this.router.navigate(['/login']);
  }

  onTrouver(): void {
    if (this.emailQuestion) {
      return;
    }
    if (!this.isStep1Valid()) {
      this.showMissingFields = true;
      this.scrollToFirstMissingField();
      return;
    }
    this.showMissingFields = false;
    this.resetSearch();
    this.searching = true;
    this.memberService
      .search(this.f.email.value)
      .pipe(timeout(3000))
      .subscribe({
        next: (results) => this.onSearchDone(results ?? []),
        error: () => {
          this.searching = false;
          this.snackbar.open('error');
        }
      });
  }

  private onSearchDone(results: Member[]): void {
    this.foundOrganisations = results.filter((r) => !!r.memberId && !!r.displayName);
    this.orgWasFound = this.foundOrganisations.length > 0;
    this.searching = false;
    this.searched = true;
    this.applyConditionalValidators();
  }

  onSelectOrganisation(value: string): void {
    this.referents = [];
    this.isOther = value === 'autre';
    if (!this.isOther) {
      this.loadReferents(value);
    }
    this.applyConditionalValidators();
  }

  private loadReferents(memberId: string): void {
    this.memberService.getReferents(memberId).subscribe({
      next: (names) => {
        if (this.f.organisationTrouvee.value === memberId) {
          this.referents = names;
        }
      },
      error: () => this.snackbar.open('error')
    });
  }

  openConfirm(): void {
    if (!this.f.cgu.value) {
      return;
    }
    if (this.form.invalid) {
      this.showMissingFields = true;
      this.scrollToFirstMissingField();
      return;
    }
    this.showMissingFields = false;
    this.showConfirm = true;
  }

  closeConfirm(): void {
    this.showConfirm = false;
  }

  confirmCreation(): void {
    this.loading = true;
    const payload = this.buildRegisterPayload();
    const pending = !payload.memberId ? 'new-organisation' : this.referents.length ? 'referent' : 'fntp';
    this.accountService
      .register(payload)
      .pipe(timeout(3000))
      .subscribe({
        next: () => {
          this.loading = false;
          this.showConfirm = false;
          this.router.navigate(['/login'], { state: { inscriptionPending: pending } });
        },
        error: () => {
          this.loading = false;
          this.snackbar.open('error');
        }
      });
  }

  private buildRegisterPayload(): RegisterRequest {
    const v = this.form.value;
    const selectedOrganisation = this.foundOrganisations.find((o) => o.memberId === v.organisationTrouvee);
    const companyFields = this.isEntrepriseTp && this.orgNotFound;
    return {
      profileType: v.profileType,
      nom: v.nom,
      prenom: v.prenom,
      email: v.email,
      fonction: this.isStudent ? '' : v.fonction,
      organisation: selectedOrganisation?.displayName ?? v.organisation,
      memberId: selectedOrganisation?.memberId,
      pays: companyFields ? v.pays : null,
      siren: companyFields && this.isFrance ? v.siren : '',
      adhesion: companyFields ? v.adhesion : null,
      numeroTp: companyFields && this.isAdherente ? v.numeroTp : '',
      adresse: companyFields && this.isNonAdherente ? v.adresse : '',
      codePostal: companyFields && this.isNonAdherente ? v.codePostal : '',
      ville: companyFields && this.isNonAdherente ? v.ville : '',
      cgu: v.cgu
    };
  }

  private isStep1Valid(): boolean {
    return this.step1Fields.every((name) => this.f[name].valid);
  }

  private scrollToFirstMissingField(): void {
    const name = Object.keys(this.f).find((field) => this.f[field].invalid);
    if (!name) {
      return;
    }
    const radioGroups: Record<string, string> = { profileType: '.radio-grid', adhesion: '.radio-row' };
    const selector = radioGroups[name] ?? `[formControlName="${name}"]`;
    const element = this.elementRef.nativeElement.querySelector<HTMLElement>(selector);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (element instanceof HTMLInputElement) {
      element.focus({ preventScroll: true });
    }
  }

  private applyConditionalValidators(): void {
    const apply = (name: string, validators: ValidatorFn[] | null) => {
      this.f[name].setValidators(validators);
      this.f[name].updateValueAndValidity();
    };
    apply('fonction', this.isStudent ? null : [Validators.required]);
    ['organisation', 'organisationTrouvee', 'siren', 'adhesion', 'numeroTp', 'adresse', 'codePostal', 'ville'].forEach((name) => apply(name, null));

    if (this.orgFound) {
      apply('organisationTrouvee', [Validators.required]);
    }

    if (this.orgNotFound) {
      apply('organisation', [Validators.required]);
    }

    if (!this.isEntrepriseTp || !this.orgNotFound) {
      return;
    }
    if (this.isFrance) {
      apply('siren', [Validators.required, Validators.pattern(/^\d{9}$/), sirenKeyValidator]);
    }
    apply('adhesion', [Validators.required]);
    if (this.isAdherente) {
      apply('numeroTp', [Validators.required, Validators.maxLength(5)]);
    }
    if (this.isNonAdherente) {
      apply('adresse', [Validators.required]);
      // Certains pays n'ont pas de code postal
      apply('codePostal', this.isFrance ? [Validators.required, Validators.pattern(/^\d{5}$/)] : null);
      apply('ville', [Validators.required]);
    }
  }

  private resetSearch(): void {
    this.searched = false;
    this.searching = false;
    this.isOther = false;
    this.referents = [];
    this.f.organisationTrouvee.setValue('');
    this.f.cgu.setValue(false);
  }
}
